import React, { useState, useCallback, useContext } from 'react';

import { InjectedIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { DiffStatusIcon } from 'src/components/diff-file-status/diff-file-status-icon-mapper';
import PreviewMergeDiffIcon from 'src/components/diff-file-status/preview-merge-diff-icon';
import { DiffConflictLozenge } from 'src/components/diff-set/diff-conflict-lozenge';
import { DiffMetadataProps } from 'src/components/diff-set/diff-set';
import { useFileViewed } from 'src/components/diff-set/hooks/file-viewed';
import { getSourceUrls } from 'src/components/diff-set/image-diffs/utils';
import { FileStateless } from 'src/components/file';
import { useIntl } from 'src/hooks/intl';
import { TOGGLE_DIFF_EXPANSION } from 'src/redux/pull-request/actions';
import { getIsSingleFileModeActive } from 'src/redux/pull-request/selectors';
import { DiffActionsMenu } from 'src/sections/repository/sections/pull-request/components/diff-actions-menu';
import { DiffFeaturesContext } from 'src/sections/repository/sections/pull-request/components/diff-features-context';
import { FileLevelAddCommentButton } from 'src/sections/repository/sections/pull-request/components/file-level-comment/file-level-add-comment-button';
import { ReviewedCheckbox } from 'src/sections/repository/sections/pull-request/components/file-reviewed-checkbox/reviewed-checkbox';
import { useSingleDiffReviewed } from 'src/sections/repository/sections/pull-request/components/reviewed-files/reviewed-files-provider';
import { PullRequestFileConversation } from 'src/sections/repository/sections/pull-request/containers/conversation/pull-request-file-conversation';
import { getCurrentUserIsAnonymous } from 'src/selectors/user-selectors';
import { DiffStatStatus } from 'src/types/diffstat';
import { Diff } from 'src/types/pull-request';

import messages from './image-diff.i18n';
import * as styles from './image-diff.style';

function renderImageDiffContent(
  intl: InjectedIntl,
  file: Diff,
  beforeUrl: string,
  afterUrl: string
) {
  switch (file.fileDiffStatus) {
    case DiffStatStatus.Added: {
      const title = intl.formatMessage(messages.added);
      return <styles.ImageDiffContentAfter title={title} source={afterUrl} />;
    }
    case DiffStatStatus.Removed: {
      const title = intl.formatMessage(messages.removed);
      return <styles.ImageDiffContentBefore title={title} source={beforeUrl} />;
    }
    case DiffStatStatus.BinaryConflict:
    case DiffStatStatus.Modified: {
      const beforeTitle = intl.formatMessage(messages.before);
      const afterTitle = intl.formatMessage(messages.after);

      return (
        <styles.TwoUp>
          <styles.ImageDiffContentBefore
            title={beforeTitle}
            source={beforeUrl}
          />
          <styles.ImageDiffContentAfter title={afterTitle} source={afterUrl} />
        </styles.TwoUp>
      );
    }
    default:
      return null;
  }
}

export type ImageDiffProps = {
  filepath: string;
  file: Diff;
  isOpen: boolean;
  prevFilepath?: string;
  diffMetadataProps: DiffMetadataProps;
};

function ImageDiff(props: ImageDiffProps) {
  const { file, filepath, prevFilepath, isOpen, diffMetadataProps } = props;
  const { hasMarkFilesAsViewed, hasComments } = useContext(DiffFeaturesContext);

  const { fileDiffStatus } = file;
  const [isDiffActionsMenuOpen, setIsDiffActionsMenuOpen] = useState(false);
  const dispatch = useDispatch();

  const isSingleFileModeActive = useSelector(getIsSingleFileModeActive);

  const toggleExpanded = () =>
    dispatch({
      type: TOGGLE_DIFF_EXPANSION,
      payload: { filepath, isOpening: !isOpen },
    });

  const { afterUrl, beforeUrl } = getSourceUrls({
    file,
    sourceHash: diffMetadataProps.sourceHash,
    destinationHash: diffMetadataProps.destinationHash!,
    fileDiffStatus: file.fileDiffStatus,
    repoFullName: diffMetadataProps.sourceRepoFullSlug,
  });

  const dropdownMenu = (
    <DiffActionsMenu
      onSideBySide={undefined}
      onAddComment={undefined}
      filepath={filepath}
      diffMetadataProps={diffMetadataProps}
      isDeleted={fileDiffStatus === DiffStatStatus.Removed}
      onDiffActionsMenuClicked={e => setIsDiffActionsMenuOpen(e.isOpen)}
      isCollapsible={!isSingleFileModeActive}
    />
  );

  const intl = useIntl();
  const { isConflicted, conflictMessage } = file;
  const renderConflictLozenge = useCallback(
    () =>
      isConflicted && conflictMessage ? (
        <DiffConflictLozenge conflictMessage={conflictMessage} />
      ) : undefined,
    [isConflicted, conflictMessage]
  );

  const isCurrentUserAnonymous = useSelector(getCurrentUserIsAnonymous);

  const isReviewed = useSingleDiffReviewed(filepath);

  const onFileViewed = useFileViewed();
  const onReviewedChanged = useCallback(
    () => onFileViewed(filepath, isReviewed, file.index),
    [filepath, isReviewed, file.index, onFileViewed]
  );

  return (
    <FileStateless
      dropdownMenu={dropdownMenu}
      filePath={filepath}
      hasStickyHeader
      icon={<DiffStatusIcon fileDiffStatus={fileDiffStatus} />}
      isCollapsible={!isSingleFileModeActive}
      isDropdownMenuOpen={isDiffActionsMenuOpen}
      isExpanded={isOpen}
      isReviewed={isReviewed}
      prevFilePath={prevFilepath}
      renderAfterFilePath={renderConflictLozenge}
      previewMergeDiffIcon={<PreviewMergeDiffIcon />}
      toggleExpanded={toggleExpanded}
      renderBeforeActions={() => (
        <>
          {hasMarkFilesAsViewed ? (
            <ReviewedCheckbox
              filePath={filepath}
              onChange={onReviewedChanged}
            />
          ) : null}
          {!isCurrentUserAnonymous && hasComments ? (
            <FileLevelAddCommentButton filePath={filepath} />
          ) : null}
        </>
      )}
    >
      {hasComments && <PullRequestFileConversation file={file} />}
      {renderImageDiffContent(intl, file, beforeUrl, afterUrl)}
    </FileStateless>
  );
}

export default React.memo(ImageDiff);
